<template>
    <v-dialog :value="true" max-width="640" persistent>
        <v-card>
            <v-card-title class="headline">{{ "New version" | t }}: {{ recipe.code }}</v-card-title>
            <v-card-subtitle v-if="latestVersion" class="subtitle-1 mb-0 pb-0">
                {{ "The current version ends the day before the new one starts." | t }}
            </v-card-subtitle>
            <v-card-text class="pt-4">
                <v-form ref="form" v-model="formValid" @submit.prevent="save">
                    <v-row>
                        <v-col cols="6">
                            <v-text-field
                                v-model="validFrom"
                                type="date"
                                :min="minValidFrom"
                                :label="t('Valid from')"
                                :rules="[rules.required, rules.afterLatest]"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>

                    <v-row v-for="(line, index) in lines" :key="'line_' + line.product_id" no-gutters>
                        <v-col cols="8" class="mt-2">{{ line.product_name }} ({{ line.product_code }})</v-col>
                        <v-col cols="4">
                            <v-text-field
                                v-model="lines[index].quantity_per_mix"
                                :label="t('kg in one mix')"
                                :rules="[rules.required, rules.positive, rules.maxDecimals]"
                                inputmode="decimal"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>

                    <v-text-field
                        v-model="note"
                        :label="t('Note')"
                        :rules="[rules.maxNoteLength]"
                        counter="255"
                        outlined
                        dense
                    />
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn text :disabled="savingVersion" @click="$emit('close')">{{ "Cancel" | t }}</v-btn>
                <v-btn dark color="bal-red" :loading="savingVersion" :disabled="!formValid" @click="save">
                    {{ "Save" | t }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
// Form for adding a new version to a mix recipe (BALTT-529). The ingredients of the current version are
// prefilled; the user picks the first valid day and edits the kg per mix.
// Props: recipe (recipe object from RecipeStore, versions newest first).
// Events: close (cancelled), saved (version saved, list already reloaded).
import { mapActions, mapState } from "vuex";

const MAX_NOTE_LENGTH = 255;

// Accepts both "5,6" and "5.6" because people type the Estonian decimal comma.
function parseQuantity(value) {
    if (value === null || value === undefined) return NaN;
    const normalized = String(value).trim().replace(",", ".");
    return normalized === "" ? NaN : Number(normalized);
}

function addDays(isoDate, days) {
    const date = new Date(isoDate + "T00:00:00Z");
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

export default {
    name: "recipe-version-modal",
    props: {
        recipe: { type: Object, required: true },
    },
    data() {
        const latest = this.recipe.versions.length ? this.recipe.versions[0] : null;
        return {
            formValid: false,
            validFrom: "",
            note: "",
            lines: latest
                ? latest.lines.map((line) => ({
                      product_id: line.product_id,
                      product_code: line.product_code,
                      product_name: line.product_name,
                      quantity_per_mix: String(line.quantity_per_mix),
                  }))
                : [],
        };
    },
    computed: {
        ...mapState("RecipeStore", ["savingVersion"]),
        latestVersion() {
            return this.recipe.versions.length ? this.recipe.versions[0] : null;
        },
        // ISO dates compare correctly as strings.
        minValidFrom() {
            return this.latestVersion ? addDays(this.latestVersion.valid_from, 1) : null;
        },
        rules() {
            return {
                required: (value) => (value !== null && String(value).trim() !== "") || this.t("Required"),
                afterLatest: (value) =>
                    !this.minValidFrom || !value || value >= this.minValidFrom ||
                    this.t("Must be after the start of the current version"),
                positive: (value) => parseQuantity(value) > 0 || this.t("Must be greater than 0"),
                maxDecimals: (value) =>
                    /^\s*\d+([.,]\d{1,3})?\s*$/.test(String(value)) || this.t("At most 3 decimals"),
                maxNoteLength: (value) => !value || value.length <= MAX_NOTE_LENGTH || this.t("Too long"),
            };
        },
    },
    methods: {
        ...mapActions("RecipeStore", ["createVersion"]),
        async save() {
            if (!this.$refs.form.validate()) return;
            const saved = await this.createVersion({
                recipeId: this.recipe.id,
                validFrom: this.validFrom,
                note: this.note.trim() || null,
                lines: this.lines.map((line) => ({
                    product_id: line.product_id,
                    // Sent as a string so the backend Decimal gets exactly what was typed.
                    quantity_per_mix: String(line.quantity_per_mix).trim().replace(",", "."),
                })),
            });
            if (saved) this.$emit("saved");
        },
    },
};
</script>
